/**
 * UserGroupsPage — CRUD for user groups + member manager (epic-6 §3).
 */
import { useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Users2, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/components/shared/Toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetUserGroupsQuery,
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
  useDeleteUserGroupMutation,
  useGetUserGroupMembersQuery,
  useAddUserGroupMemberMutation,
  useRemoveUserGroupMemberMutation,
} from '@/store/api/organizationsApi';
import { useGetUsersQuery } from '@/store/api/apiSlice';
import type { UserGroup, UserGroupMember } from '@/types';
import { format } from 'date-fns';
import { formatDateSafe } from '@/lib/date';

export function UserGroupsPage() {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<UserGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserGroup | null>(null);
  const [membersGroup, setMembersGroup] = useState<UserGroup | null>(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [activeTab, setActiveTab] = useState('all');

  const { data: groups, isLoading } = useGetUserGroupsQuery();
  const [createGroup] = useCreateUserGroupMutation();
  const [updateGroup] = useUpdateUserGroupMutation();
  const [deleteGroup, { isLoading: isDeleting }] = useDeleteUserGroupMutation();

  const groupList = (groups as UserGroup[] | undefined) ?? [];

  const columns: ColumnDef<UserGroup, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Users2 className="h-4 w-4 text-muted-foreground shrink-0" />
          <div>
            <p className="font-medium">{row.original.name}</p>
            {row.original.description && (
              <p className="text-xs text-muted-foreground truncate max-w-xs">{row.original.description}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'orgId',
      header: 'Org',
      cell: ({ row }) =>
        row.original.orgId ? (
          <Badge variant="outline" className="font-mono text-[10px]">{row.original.orgId.slice(0, 8)}…</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.isActive ? 'default' : 'secondary'} className="text-xs">
          {row.original.isActive ? 'active' : 'inactive'}
        </Badge>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setMembersGroup(row.original); }} title="Members">
            <UserPlus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditing(null);
    setForm({ name: '', description: '' });
    setModalOpen(true);
  }

  function openEdit(g: UserGroup) {
    setEditing(g);
    setForm({ name: g.name, description: g.description ?? '' });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    const body = { name: form.name.trim(), description: form.description.trim() || undefined };
    try {
      if (editing) {
        await updateGroup({ id: editing._id, body }).unwrap();
      } else {
        await createGroup(body).unwrap();
      }
      setModalOpen(false);
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to save group');
    }
  }

  async function handleDelete() {
    if (deleteTarget) {
      try {
        await deleteGroup(deleteTarget._id).unwrap();
        setDeleteTarget(null);
      } catch (err) {
        toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to delete group');
      }
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Groups</h1>
          <p className="text-sm text-muted-foreground">
            Define reusable user groups and manage their members
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Create Group
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <p className="text-sm text-muted-foreground">
            Click the user icon on a row to manage its members.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : groupList.length === 0 ? (
            <EmptyState
              title="No user groups"
              description="Create a group to organize users"
              action={<Button onClick={openCreate}>Create Group</Button>}
            />
          ) : (
            <DataTable columns={columns} data={groupList} />
          )}
        </CardContent>
      </Card>

      {membersGroup && (
        <GroupMembersDialog group={membersGroup} onClose={() => setMembersGroup(null)} activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Group' : 'Create Group'}</DialogTitle>
            <DialogDescription>Define the group name and an optional description.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Group"
        description={`Delete "${deleteTarget?.name}" and its membership?`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}

// ─── Members manager dialog ───────────────────────────────────────────────────

function GroupMembersDialog(props: { group: UserGroup; onClose: () => void; activeTab: string; onTabChange: (t: string) => void }) {
  const toast = useToast();
  const [addMember] = useAddUserGroupMemberMutation();
  const [removeMember] = useRemoveUserGroupMemberMutation();
  const { data: members, isLoading } = useGetUserGroupMembersQuery(props.group._id);
  const { data: users } = useGetUsersQuery({ page: 1, pageSize: 100 });
  const [userId, setUserId] = useState('');

  const memberList = (members as UserGroupMember[] | undefined) ?? [];
  const userList =
    ((users as { data?: Array<{ _id: string; username: string; email?: string }> } | undefined)?.data ?? []) as Array<{
      _id: string;
      username: string;
      email?: string;
    }>;

  const userOf = (id: string) => userList.find((u) => u._id === id);
  const available = userList.filter((u) => !memberList.some((m) => m.userId === u._id));

  const submit = async () => {
    if (!userId) return;
    try {
      await addMember({ id: props.group._id, userId }).unwrap();
      setUserId('');
      toast.success('Member added');
    } catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to add member');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users2 className="h-5 w-5" /> Members — {props.group.name}
          </DialogTitle>
          <DialogDescription>Add or remove users from this group.</DialogDescription>
        </DialogHeader>
        <Tabs className="min-w-0" value={props.activeTab} onValueChange={props.onTabChange}>
          <TabsList>
            <TabsTrigger value="all">All ({memberList.length})</TabsTrigger>
            <TabsTrigger value="add">Add member</TabsTrigger>
          </TabsList>
          <TabsContent value="all">
            {isLoading ? (
              <LoadingSpinner className="py-6" />
            ) : memberList.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No members yet.</p>
            ) : (
              <div className="space-y-1.5">
                {memberList.map((m) => (
                  <div key={m.userId} className="flex items-center justify-between rounded-md border px-3 py-1.5 text-sm">
                    <span>{userOf(m.userId)?.username ?? m.userId}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">{formatDateSafe(m.addedAt, 'MMM d')}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={async () => {
                          try {
                            await removeMember({ id: props.group._id, userId: m.userId }).unwrap();
                            toast.success('Member removed');
                          } catch (err) {
                            toast.error((err as { data?: { message?: string } })?.data?.message ?? 'Failed to remove');
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          <TabsContent value="add" className="space-y-3">
            <div className="flex gap-2">
              <select
                className="flex h-10 min-w-0 flex-1 rounded-md border border-input bg-background px-3 text-sm"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              >
                <option value="">— select user —</option>
                {available.map((u) => (
                  <option key={u._id} value={u._id}>{u.username} ({u.email ?? ''})</option>
                ))}
              </select>
              <Button onClick={submit} disabled={!userId}><UserPlus className="mr-1 h-4 w-4" /> Add</Button>
            </div>
            {available.length === 0 && (
              <p className="text-xs text-muted-foreground">Every user is already a member.</p>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}